'use client';

import {
  PAGE_SLUG,
  resolveFooterLegalLinks,
  type EditTarget,
  type LayoutBlock,
  type PageMeta,
} from '@web-studio/shared-schemas';
import { openSiteCookieSettings } from '../../chrome/SiteCookieSettingsBar';
import { EditableContent } from '../../edit/EditableContent';
import { pickBlockField } from '../../lib/localized';
import { pickChrome } from '../../lib/siteChromeCopy';

const LEGAL_LABEL_FIELD: Record<string, string> = {
  [PAGE_SLUG.impressum]: 'imprintLabel',
  [PAGE_SLUG.datenschutz]: 'privacyLabel',
};

interface FooterLegalLinksProps {
  pages?: PageMeta[];
  locale: string;
  defaultLocale?: string;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  /** Footer block content — optional label overrides (`imprintLabel`, `privacyLabel`, `cookieSettingsLabel`). */
  content?: LayoutBlock['content'];
  onNavigateToPageSlug?: (slug: string) => void;
}

export function FooterLegalLinks({
  pages,
  locale,
  defaultLocale = locale,
  editable = false,
  activeEditTarget = null,
  content,
  onNavigateToPageSlug,
}: FooterLegalLinksProps) {
  const links = resolveFooterLegalLinks(pages, locale, defaultLocale);
  // Mirror NavigationHeader: shell browse callback is only passed in view mode.
  const allowNavigation = Boolean(onNavigateToPageSlug) && !editable;

  const resolveLabel = (slug: string, fallback: string) => {
    const fieldKey = LEGAL_LABEL_FIELD[slug];
    if (!fieldKey || !content) {
      return fallback;
    }
    return pickBlockField(content, fieldKey, locale).trim() || fallback;
  };

  const cookieLabel =
    (content ? pickBlockField(content, 'cookieSettingsLabel', locale).trim() : '') ||
    pickChrome('cookieSettingsLabel', locale);

  return (
    <nav className="ws-footer__legal-nav" aria-label={pickChrome('legalNav', locale)}>
      <ul className="ws-footer__legal-links">
        {links.map((link) => {
          const fieldKey = LEGAL_LABEL_FIELD[link.slug] ?? link.slug;
          return (
            <li key={link.slug}>
              <EditableContent
                as="a"
                fieldKey={fieldKey}
                editable={editable}
                activeEditTarget={activeEditTarget}
                className="ws-footer__legal-link"
                href={allowNavigation ? '#' : link.href}
                onClick={
                  allowNavigation
                    ? (event) => {
                        event.preventDefault();
                        onNavigateToPageSlug!(link.slug);
                      }
                    : undefined
                }
              >
                {resolveLabel(link.slug, link.label)}
              </EditableContent>
            </li>
          );
        })}
        <li>
          <EditableContent
            as="button"
            type="button"
            fieldKey="cookieSettingsLabel"
            editable={editable}
            activeEditTarget={activeEditTarget}
            className="ws-footer__legal-link ws-footer__cookie-settings"
            {...(!editable ? { 'data-ws-cookie-settings-open': true } : {})}
            onClick={(event) => {
              if (editable) {
                return;
              }
              event.preventDefault();
              openSiteCookieSettings();
            }}
          >
            {cookieLabel}
          </EditableContent>
        </li>
      </ul>
    </nav>
  );
}
